import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { monthsToYears } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChargerEV, ResizingArrow, SortingArrowDown, SortingArrowUp } from '../../assets/icons';
import { IconSize } from '../../constant/IconSize.constant';
import { NUMBER } from '../../constant/Number.constant';
import { useCharger } from '../../hooks/useChargers';
import { useLicense } from '../../hooks/useLicense';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import { License } from '../../stores/types';
import { convertToDate, formatDate } from '../../utils/Date.Util';
import { ColorType, Icon, Label, LabelType, Pill } from '../_ui';
import '../_ui/data-table/dataTable.css';
import { captureSortColumnEvent, DataTableName, DataTablePage } from '../_ui/data-table/utils';
import { GridPager } from '../_ui/grid/Grid-Pager.component';
import LicenseStatus from './LicenseStatus.component';
import NoLicense from './NoLicense.component';
import SupportLicenseGridMenu from './SupportLicenseGridMenu.component';
import { getLicenseType, getRemainingDays, isNoDurationType, LICENSE_STATUS } from './utils';

const ROW_PER_PAGE = NUMBER.TWENTY;
const LICENSE_TABLE_SORT_CONFIG_STORAGE_KEY = 'licenseTable.sortConfig';

const LicensesGrid = () => {
  const { t } = useTranslation();
  const { getChargerNameById } = useCharger();
  const { navigateWithCompanyId } = useNavigateWithSearchParam();
  const {
    licenseFilterquery,
    licensesData,
    handleLoadPage,
    licensesDataCount,
    isLicenseLoading,
    columnVisibility,
    setHandleResetColumnSizing,
    setExportDataGetter,
  } = useLicense();
  const getRemainings = useCallback(
    (expirationDate: Date, status: string) => {
      if (status.toLowerCase() === LICENSE_STATUS.EXPIRED) {
        return t(LICENSE_STATUS.EXPIRED);
      }
      const { years, days } = getRemainingDays(new Date(), expirationDate);
      const noOfYearsDays = years > 0 ? `${years}y ${days}d` : `${days}d`;
      return t('license_grid_remaining', { noOfYearsDays });
    },
    [t],
  );
  const totalPages = Math.ceil(licensesDataCount / ROW_PER_PAGE);

  const [sorting, setSorting] = useLocalStorageState<SortingState>(
    LICENSE_TABLE_SORT_CONFIG_STORAGE_KEY,
    [],
  );

  const columns = useMemo<ColumnDef<License>[]>(
    () => [
      {
        accessorKey: 'licenseKey',
        header: t('license_key'),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <Label
              isLoading={isLicenseLoading}
              className='whitespace-nowrap truncate'
              text={row.original.licenseKey}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          );
        },
        size: 150,
        minSize: 150,
        enableSorting: false,
      },
      {
        accessorKey: 'status',
        accessorFn: (row) => row.status?.toLowerCase() || '',
        header: t('status'),
        enableResizing: true,
        cell: ({ row }) => {
          return <LicenseStatus isLoading={isLicenseLoading} status={row.original.status} />;
        },
        size: 120,
        minSize: 120,
        sortingFn: 'text',
        enableSorting: true,
      },
      {
        accessorKey: 'assignedCharger',
        header: t('charger'),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            row.original.assignedCharger &&
            (getChargerNameById(row.original.assignedCharger) || isLicenseLoading) && (
              <Pill
                isLoading={isLicenseLoading}
                iconLeft={ChargerEV}
                label={getChargerNameById(row.original.assignedCharger)}
                onClick={() =>
                  navigateWithCompanyId(
                    `/chargers/${row.original.assignedCharger}`,
                    row.original.companyId,
                  )
                }
                labelClass='cursor-pointer'
              />
            )
          );
        },
        size: 150,
        minSize: 150,
        enableSorting: false,
      },
      {
        accessorKey: 'type',
        //function extracts/transforms data from each row and caches the result.
        accessorFn: (row) => t(getLicenseType(row.type)),
        header: t('license_type'),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <Label
              className='w-[60px] items-center'
              isLoading={isLicenseLoading}
              text={t(getLicenseType(row.original.type))}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          );
        },
        size: 120,
        minSize: 120,
        sortingFn: 'text',
        enableSorting: true,
      },
      {
        accessorKey: 'ports',
        header: t('ports'),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <Label
              isLoading={isLicenseLoading}
              text={row.original.ports}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          );
        },
        size: 80,
        minSize: 80,
        enableSorting: false,
      },
      {
        accessorKey: 'includes4G',
        header: t('license_inc_4g'),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <Label
              isLoading={isLicenseLoading}
              text={t(row.original.includes4G ? 'yes' : 'no')}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          );
        },
        size: 100,
        minSize: 100,
        enableSorting: false,
      },
      {
        accessorKey: 'duration',
        header: t('duration'),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className='overflow-hidden text-ellipsis items-center'>
              {isNoDurationType(row.original.type, row.original.includes4G) ? (
                <Label
                  isLoading={isLicenseLoading}
                  text={t('duration_not_available')}
                  type={LabelType.BODY3}
                  color={ColorType.BLACK}
                />
              ) : (
                <Label
                  isLoading={isLicenseLoading}
                  text={
                    row.original.duration === 1
                      ? t('license_grid_duration_months', { number: 1 })
                      : t('license_grid_duration', { number: monthsToYears(row.original.duration) })
                  }
                  type={LabelType.BODY3}
                  color={ColorType.BLACK}
                />
              )}
            </div>
          );
        },
        size: 120,
        minSize: 120,
        enableSorting: false,
      },
      {
        accessorKey: 'remaining',
        header: t('license_remaining'),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className='overflow-hidden text-ellipsis items-center'>
              {row.original.expirationDate &&
                !isNoDurationType(row?.original.type, row?.original.includes4G) && (
                  <Label
                    isLoading={isLicenseLoading}
                    text={getRemainings(row.original.expirationDate, row.original.status)}
                    type={LabelType.BODY3}
                    color={ColorType.BLACK}
                  />
                )}
            </div>
          );
        },
        size: 120,
        minSize: 120,
        enableSorting: false,
      },
      {
        accessorKey: 'expirationDate',
        header: t('expires'),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className='overflow-hidden text-ellipsis items-center w-[100px]'>
              {row.original.expirationDate &&
                !isNoDurationType(row?.original.type, row?.original.includes4G) && (
                  <Label
                    isLoading={isLicenseLoading}
                    text={formatDate(convertToDate(row.original.expirationDate), 'MMM d, yyyy')}
                    type={LabelType.BODY3}
                    color={ColorType.BLACK}
                  />
                )}
            </div>
          );
        },
        size: 120,
        minSize: 120,
        enableSorting: false,
      },
      {
        accessorKey: 'orderId',
        header: t('order_id'),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <Label
              isLoading={isLicenseLoading}
              className='whitespace-nowrap truncate'
              text={row.original.orderId}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          );
        },
        size: 150,
        minSize: 150,
        enableSorting: false,
      },
      {
        accessorKey: 'action',
        header: '',
        enableResizing: false,
        cell: ({ row }) => {
          return (
            <div className='flex justify-end items-start'>
              <SupportLicenseGridMenu license={row.original} />
            </div>
          );
        },
        size: 80,
        minSize: 80,
        enableSorting: false,
      },
    ],
    [],
  );

  const licenseTable = useReactTable({
    data: licensesData,
    columns: columns,
    state: {
      columnVisibility,
      sorting,
      pagination: {
        pageIndex: licenseFilterquery.page - 1,
        pageSize: ROW_PER_PAGE,
      },
    },
    onSortingChange: (updaterOrValue) => {
      // Get the new sorting state
      const newSorting =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;

      // Capture the sort column event
      if (newSorting.length > 0 && newSorting[0]) {
        captureSortColumnEvent(DataTablePage.LICENSES, DataTableName.LICENSES, newSorting[0].id);
      }

      // Update the sorting state
      setSorting(newSorting);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    manualPagination: false,
  });

  // Set the export data getter to return data after sorting is applied
  useEffect(() => {
    setExportDataGetter(() => {
      return licenseTable.getSortedRowModel().rows.map((row) => row.original);
    });
  }, [licenseTable, setExportDataGetter]);

  useEffect(() => {
    setHandleResetColumnSizing(() => licenseTable.resetColumnSizing());
  }, [setHandleResetColumnSizing, licenseTable]);

  return (
    <div className='overflow-hidden'>
      {licensesDataCount || isLicenseLoading ? (
        <div className='overflow-visible mt-5'>
          <div className='overflow-x-auto overflow-y-visible data-table-scroll'>
            <table className='data-table'>
              <thead>
                {licenseTable.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className='bg-white'>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        data-column-id={header.column.id}
                        style={{
                          width: header.getSize(),
                          minWidth: header.column.columnDef.minSize ?? 100,
                          position: 'relative',
                        }}
                        className='px-4 py-3 text-left text-sm font-semibold font-inter text-grey6'
                      >
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={
                            header.column.getCanSort()
                              ? header.column.getToggleSortingHandler()
                              : undefined
                          }
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {/* Sort arrows */}
                          {header.column.getCanSort() && (
                            <button
                              type='button'
                              className='absolute right-0 top-1/2 -translate-y-1/2 flex flex-col pr-2'
                            >
                              <Icon
                                src={SortingArrowUp}
                                size={IconSize.SIZE_12x12}
                                color={
                                  header.column.getIsSorted() === 'asc'
                                    ? ColorType.GREY6
                                    : ColorType.GREY3
                                }
                              />
                              <Icon
                                src={SortingArrowDown}
                                size={IconSize.SIZE_12x12}
                                color={
                                  header.column.getIsSorted() === 'desc'
                                    ? ColorType.GREY6
                                    : ColorType.GREY3
                                }
                                className='-mt-1'
                              />
                            </button>
                          )}
                        </div>

                        {/* Resize Handle */}
                        {header.column.getCanResize() && (
                          <div
                            onDoubleClick={() => header.column.resetSize()}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className='resizer'
                            style={{
                              cursor: `url(${ResizingArrow}) 8 8, col-resize`,
                            }}
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {licenseTable.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                        }}
                        className='px-4 py-3 text-sm text-gray-900 overflow-hidden whitespace-nowrap'
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <GridPager
              totalPage={totalPages}
              currentPage={licenseFilterquery.page}
              loadPage={handleLoadPage}
              showPageSearch
            />
          )}
        </div>
      ) : (
        <NoLicense isLoading={isLicenseLoading} isDataExists />
      )}
    </div>
  );
};

export default LicensesGrid;
